import { afterRenderEffect, Component, computed, ElementRef, input, untracked, viewChild } from '@angular/core';

/**
 * - IDLE: slowly swaying around the board
 * - FOCUS: all aimed at the board, while the tiles are revealed
 * - PARTY: sweeping wildly, the word was guessed
 * - DIM: dimmed and hanging down, the word wasn't guessed
 */
export type TLingoSpotlightMood = 'IDLE' | 'FOCUS' | 'PARTY' | 'DIM';

const PARTY_SWEEP = 20;
const PARTY_SWEEP_DURATION = 1400;

const BEAM_MOODS: Record<TLingoSpotlightMood, string> = {
  IDLE: 'opacity-55',
  FOCUS: 'opacity-55',
  PARTY: 'opacity-80',
  DIM: 'opacity-[0.08]',
};

const LENS_GLOW = 'shadow-[0_8px_48px_12px_color-mix(in_srgb,var(--color)_70%,transparent)]';
const LENS_MOODS: Record<TLingoSpotlightMood, string> = {
  IDLE: LENS_GLOW,
  FOCUS: LENS_GLOW,
  PARTY: LENS_GLOW,
  DIM: 'shadow-none brightness-45 saturate-60',
};

/**
 * A studio spotlight hanging from the ceiling: a lamp on a rod, with a beam of light. The lamp and its beam turn together.
 * Place it with `left`/`top` on the spot it hangs from; `scale` scales it around that spot.
 */
@Component({
  selector: 'rid-lingo-spotlight',
  templateUrl: './rid-lingo-spotlight.html',
  host: {
    class: 'pointer-events-none absolute top-0 size-0',
    '[style.--color]': 'color()',
    '[style.--drop]': 'drop()',
    '[style.--offset]': 'offset()',
  },
})
export class RidLingoSpotlight {
  color = input.required<string>();
  /** The angle (clockwise from straight down) at which the lamp points at the board. */
  focusAngle = input<number>(0);
  /** How far the lamp hangs below the ceiling, in Tailwind spacing units. */
  drop = input<number>(1);
  mood = input<TLingoSpotlightMood>('IDLE');
  /** Desyncs the idle animations of the spotlights, so they don't all sway in unison. */
  offset = input<number>(0);

  // Only roughly towards the board while idle, so the lamps don't all stare at it; hanging down when dimmed
  aim = computed(() => {
    const mood = this.mood();
    if (mood === 'DIM') return 0;
    return mood === 'IDLE' ? this.focusAngle() * 0.8 : this.focusAngle();
  });

  beamClass = computed(() => BEAM_MOODS[this.mood()]);
  lensClass = computed(() => LENS_MOODS[this.mood()]);
  isPartying = computed(() => this.mood() === 'PARTY');

  private readonly sweep = viewChild.required<ElementRef<HTMLElement>>('sweep');
  private sweepAnimation?: Animation;

  constructor() {
    // The party sweep is animated from script, so it can ease in from (and back out to) wherever the lamp is at that moment
    afterRenderEffect(() => {
      const party = this.mood() === 'PARTY';
      untracked(() => (party ? this.startSweeping() : this.stopSweeping()));
    });
  }

  private startSweeping() {
    if (this.sweepAnimation || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const element = this.sweep().nativeElement;
    // Every other spotlight sweeps the other way, so they cross each other
    const angle = this.offset() % 2 === 0 ? PARTY_SWEEP : -PARTY_SWEEP;

    // Swings out to one side first, then keeps sweeping from side to side
    const intro = element.animate([{ transform: 'rotate(0deg)' }, { transform: `rotate(${angle}deg)` }], {
      duration: PARTY_SWEEP_DURATION / 2,
      easing: 'ease-in-out',
      fill: 'forwards',
    });
    this.sweepAnimation = intro;

    intro.onfinish = () => {
      if (this.sweepAnimation !== intro) return;

      // Eased per keyframe, so the lamp slows down at both sides
      this.sweepAnimation = element.animate(
        [
          { transform: `rotate(${angle}deg)`, easing: 'ease-in-out' },
          { transform: `rotate(${-angle}deg)`, easing: 'ease-in-out' },
          { transform: `rotate(${angle}deg)` },
        ],
        { duration: PARTY_SWEEP_DURATION * 2, iterations: Infinity },
      );
      intro.cancel();
    };
  }

  private stopSweeping() {
    if (!this.sweepAnimation) return;

    const element = this.sweep().nativeElement;
    const current = getComputedStyle(element).transform;
    this.sweepAnimation.cancel();
    this.sweepAnimation = undefined;

    element.animate([{ transform: current }, { transform: 'none' }], { duration: 900, easing: 'ease-in-out' });
  }
}
